'use client';
import { startTransition, useEffect, useState } from 'react';
import { useWatchlistContext } from '@benzinga/user-context';
import { Watchlist } from '@benzinga/watchlist-manager';
import { Dropdown } from '@benzinga/core-ui';
import { FaChevronDown } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';

import { IoAddSharp, IoLogoBuffer } from 'react-icons/io5';

export const WatchlistSelector = () => {
  const {
    addTickerModal,
    deleteModal,
    deleteTickerModal,
    editModal,
    selectedWatchlist,
    setEditModal,
    setSelectedWatchlist,
    setShowCreateModal,
    showCreateModal,
    showNotificationsModal,
    showYodleeModal,
    overviewWatchlist,
    watchlists,
  } = useWatchlistContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (
      addTickerModal.show ||
      deleteModal.show ||
      deleteTickerModal.show ||
      editModal.show ||
      showCreateModal ||
      showNotificationsModal.show ||
      showYodleeModal
    ) {
      setIsDropdownOpen(false);
    }
  }, [
    addTickerModal.show,
    deleteModal.show,
    deleteTickerModal.show,
    editModal.show,
    showCreateModal,
    showNotificationsModal.show,
    showYodleeModal,
  ]);

  const handleDropdownClick = (watchlist: Watchlist) => {
    startTransition(() => {
      setSelectedWatchlist(watchlist);
    });
    setIsDropdownOpen(false);
  };

  const handleEditWatchlistClick = (watchlist: Watchlist) => {
    setEditModal({ show: true, watchlist });
    setIsDropdownOpen(false);
  };

  const handleAddWatchlistClick = () => {
    setShowCreateModal(true);
    setIsDropdownOpen(false);
  };

  return (
    <Dropdown
      className="min-w-0"
      closeWhenClickOutside={true}
      headless={true}
      trigger="click"
      open={isDropdownOpen}
      onToggle={setIsDropdownOpen}
      target={
        <button
          type="button"
          className="group flex h-7 min-w-0 items-center gap-1.5 rounded-md text-left transition-colors duration-150 ease-out hover:text-bzblue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bzblue-700"
        >
          <span className="truncate text-xl font-bold tracking-tight text-slate-950 group-hover:text-bzblue-800">
            {selectedWatchlist?.watchlistId === 'overview' ? 'All watchlists' : selectedWatchlist?.name}
          </span>
          <FaChevronDown
            className={`shrink-0 text-slate-400 transition-transform duration-150 ease-out group-hover:text-bzblue-700 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
            size={11}
          />
        </button>
      }
      targetFullWidth={false}
    >
      <div className="min-w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="max-h-[min(280px,45vh)] overflow-y-auto overscroll-contain py-1">
          {overviewWatchlist && (
            <button
              type="button"
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bzblue-100 ${
                overviewWatchlist.watchlistId === selectedWatchlist?.watchlistId
                  ? 'font-semibold text-bzblue-800'
                  : 'text-slate-700'
              }`}
              onClick={() => handleDropdownClick(overviewWatchlist)}
            >
              <IoLogoBuffer size={16} />
              All watchlists
            </button>
          )}
          {watchlists.map(watchlist => {
            const isSelected = watchlist.watchlistId === selectedWatchlist?.watchlistId;
            return (
              <div key={watchlist.watchlistId} className="flex items-center gap-1 pl-3 pr-2 hover:bg-bzblue-100">
                <button
                  type="button"
                  className={`flex min-w-0 flex-1 items-center justify-between gap-3 py-2 text-left text-sm ${
                    isSelected ? 'font-semibold text-bzblue-800' : 'text-slate-700'
                  }`}
                  onClick={() => handleDropdownClick(watchlist)}
                >
                  <span className="truncate">{watchlist.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-slate-400">{watchlist.symbols.length}</span>
                </button>
                <button
                  type="button"
                  aria-label={`Edit ${watchlist.name}`}
                  className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-[background-color,color,transform] duration-150 ease-out hover:bg-white hover:text-bzblue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-bzblue-700"
                  onClick={() => handleEditWatchlistClick(watchlist)}
                >
                  <FiEdit2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
        <div className="border-t border-slate-200">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-bzblue-100"
            onClick={handleAddWatchlistClick}
          >
            <IoAddSharp size={16} />
            New watchlist
          </button>
        </div>
      </div>
    </Dropdown>
  );
};
